import React from 'react';
import {
    DetailOrderTitle,
    DetailOrderToolbar,
    useSelectedOrderContext,
} from '@europrocurement/l2d-modules';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { jsonLdIdScraper } from '@europrocurement/l2d-utils';
import { prescripteursBasePath } from '../../../Prescripteurs/constants/paths';

type DetailOrderHeaderProps = {
    displayDetailOrder?: boolean;
};
const DetailOrderHeader: React.FunctionComponent<DetailOrderHeaderProps> = function ({
    displayDetailOrder = true,
}) {
    const { order, dossiers } = useSelectedOrderContext();

    /**
     * Récupère la dénomination de société du premier dossier.
     * Même logique métier que pour societeRef.
     *
     * @param dossiersList - Collection de dossiers contenant chacun une propriété societeDenomination
     * @returns La dénomination société du premier dossier, ou undefined si aucun dossier valide
     */
    const getSocieteDenomination = (
        dossiersList: Array<DossierOpenSearch>,
    ): string | null | undefined => {
        if (!dossiersList || dossiersList.length === 0) {
            return undefined;
        }

        const firstValidDenomination = dossiersList.find(
            (dossier) => dossier.societeDenomination && dossier.societeDenomination.trim() !== '',
        )?.societeDenomination;

        return firstValidDenomination;
    };

    /**
     * Détermine le statut global de la commande basé sur les statuts des dossiers.
     */
    const getOrderStatus = (dossiersList: Array<DossierOpenSearch>): string => {
        if (!dossiersList || dossiersList.length === 0) {
            return 'Indéterminé';
        }

        const statuts = dossiersList
            .map((dossier) => dossier.statutLibelle)
            .filter((statut): statut is string => Boolean(statut));

        if (statuts.length === 0) {
            return 'Indéterminé';
        }

        // Si tous les dossiers ont le même statut
        const uniqueStatuts = [...new Set(statuts)];
        if (uniqueStatuts.length === 1) {
            return uniqueStatuts[0];
        }

        // Si statuts mixtes, retourner un statut composite ou le premier
        return 'En cours'; // ou statuts[0] selon la logique métier souhaitée
    };

    /**
     * Récupère les informations du prescripteur.
     */
    const getPrescripteurInfo = (dossiersList: Array<DossierOpenSearch>) => {
        if (!dossiersList || dossiersList.length === 0) {
            return undefined;
        }

        const firstDossier = dossiersList[0];

        const id = order.prescripteur ? jsonLdIdScraper(order.prescripteur) : undefined;
        const nom = firstDossier.prescripteurLibelle;
        const codeComptable = firstDossier.prescripteurCodeCompta;

        return {
            id: id || 0,
            nom: nom || 'Non défini',
            codeComptable: codeComptable || 'N/A',
        };
    };

    /**
     * Récupère les informations de l'offre.
     */
    const getOfferInfo = (dossiersList: Array<DossierOpenSearch>) => {
        if (!dossiersList || dossiersList.length === 0) {
            return undefined;
        }

        const firstDossier = dossiersList[0];

        const id = firstDossier.offreId;
        const libelle = firstDossier.offreLibelle;

        return {
            id: id || 0,
            libelle: libelle || 'Non défini',
        };
    };

    // Données calculées pour l'affichage
    const societeDenomination = getSocieteDenomination(dossiers);
    const orderStatus = getOrderStatus(dossiers);
    const prescripteurInfo = getPrescripteurInfo(dossiers);
    const offerInfo = getOfferInfo(dossiers);

    const prescripteurDetailUrl = React.useMemo(() => {
        if (!prescripteursBasePath || !prescripteurInfo?.id) return undefined;
        return `/${prescripteursBasePath}/liste/${prescripteurInfo.id}`;
    }, [prescripteurInfo?.id]);

    return (
        <>
            <DetailOrderToolbar
                id={order.id as number}
                reference={order.numero as string}
            />
            {displayDetailOrder && (
                <DetailOrderTitle
                    color="primary"
                    icon="folder-tree"
                    title={societeDenomination || `Commande #${order.numero}`}
                    orderStatus={orderStatus}
                    prescripteur={prescripteurInfo}
                    prescripteurDetailUrl={prescripteurDetailUrl}
                    offre={offerInfo}
                />
            )}
        </>
    );
};

export default DetailOrderHeader;
